import type {
  AssignmentPointsItem,
  AssignmentPointsSettings,
  AssignmentStatusValue,
  AssignmentStudentStatus,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../types/assignmentPointsNote';
import { displayClassMemberName } from './classMemberNameDisplay';

const COMPLETED: AssignmentStatusValue[] = ['confirmed', 'delayed', 'exempted'];

/** 현재 학급 등 멤버 집합으로 배정 ID를 한정 (없으면 전체) */
export function scopedAssignedMemberIds(
  assignedMemberIds: string[],
  memberIdSet?: Set<string> | null
): string[] {
  if (!memberIdSet) return assignedMemberIds;
  return assignedMemberIds.filter((id) => memberIdSet.has(id));
}

/** Educody 규칙: 확인=base 전체, 지연/예외=%만 적용, 미확인=0 */
export function pointsForStatus(
  basePoints: number,
  status: AssignmentStatusValue,
  settings: AssignmentPointsSettings,
  overridePoints?: number | null
): number {
  if (overridePoints != null && !Number.isNaN(overridePoints)) return overridePoints;
  if (status === 'pending') return 0;
  if (status === 'confirmed' || status === 'submitted') return basePoints;
  if (status === 'delayed') {
    return Math.round(basePoints * ((settings.statusDefaultPoints.delayed || 0) / 100));
  }
  if (status === 'exempted') {
    return Math.round(basePoints * ((settings.statusDefaultPoints.exempted || 0) / 100));
  }
  return 0;
}

export function countGeneralPending(
  item: GeneralAssignmentItem,
  memberIdSet?: Set<string> | null
): number {
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  let pending = 0;
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    const st = item.studentStatuses.find((s) => s.studentId === id);
    const status = st?.status ?? 'pending';
    if (status === 'pending' || status === 'submitted') pending += 1;
  }
  return pending;
}

export function isGeneralComplete(
  item: GeneralAssignmentItem,
  memberIdSet?: Set<string> | null
): boolean {
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  return ids.length > 0 && countGeneralPending(item, memberIdSet) === 0;
}

export function projectCompletionRate(
  item: ProjectAssignmentItem,
  memberIdSet?: Set<string> | null
): number {
  const subCount = item.subTasks.length;
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  const memberCount = ids.length;
  if (subCount === 0 || memberCount === 0) return 0;
  let done = 0;
  const total = subCount * memberCount;
  for (const sub of item.subTasks) {
    for (const mid of ids) {
      const key = `${sub.id}:${mid}`;
      const st = item.studentStatusesBySubTask[key];
      if (st && COMPLETED.includes(st.status)) done += 1;
    }
  }
  return Math.round((done / total) * 100);
}

export function countProjectPending(
  item: ProjectAssignmentItem,
  memberIdSet?: Set<string> | null
): number {
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  let pending = 0;
  for (const sub of item.subTasks) {
    for (const mid of ids) {
      const st = item.studentStatusesBySubTask[`${sub.id}:${mid}`];
      const status = st?.status ?? 'pending';
      if (status === 'pending' || status === 'submitted') pending += 1;
    }
  }
  return pending;
}

/** 배정 멤버·하위과제가 있고 미확인(미제출 포함)이 없으면 완료 */
export function isProjectComplete(
  item: ProjectAssignmentItem,
  memberIdSet?: Set<string> | null
): boolean {
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  return ids.length > 0 && item.subTasks.length > 0 && countProjectPending(item, memberIdSet) === 0;
}

/** start~end 사이 routineDays에 해당하는 YYYY-MM-DD 목록 */
export function calculateRoutineDates(
  startDate: string,
  dueDate: string,
  routineDays: number[]
): string[] {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const daySet = new Set(routineDays);
  const out: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    if (daySet.has(cur.getDay())) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      out.push(`${y}-${m}-${d}`);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function ensureRoutineDateBuckets(item: RoutineAssignmentItem): RoutineAssignmentItem {
  const dates = calculateRoutineDates(item.startDate, item.dueDate, item.routineDays);
  const dateSet = new Set(dates);
  const byDate = new Map(item.dateStatuses.map((b) => [b.date, b]));
  const routineBuckets = dates.map((date) => {
    const existing = byDate.get(date);
    return existing
      ? { ...existing, date, isCustomDate: false }
      : { date, studentStatuses: [], isCustomDate: false };
  });
  const customBuckets = item.dateStatuses
    .filter((b) => b.isCustomDate && !dateSet.has(b.date))
    .map((b) => ({ ...b, isCustomDate: true }));
  // 루틴 스케줄 날짜 + 명시적 커스텀 날짜만 유지 (스케줄에서 빠진 날짜는 제거)
  return { ...item, dateStatuses: [...routineBuckets, ...customBuckets] };
}

export type ProjectSubTaskSummary = {
  subTaskId: string;
  title: string;
  points: number;
  order: number;
  totalStudents: number;
  pendingStudents: number;
  submittedStudents: number;
};

export function summarizeProjectSubTasks(
  item: ProjectAssignmentItem,
  memberIdSet?: Set<string> | null
): ProjectSubTaskSummary[] {
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  const total = ids.length;
  return item.subTasks
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((sub) => {
      let pending = 0;
      let submitted = 0;
      for (const mid of ids) {
        const st = item.studentStatusesBySubTask[`${sub.id}:${mid}`];
        const status = st?.status ?? 'pending';
        if (status === 'pending') pending += 1;
        else if (status === 'submitted') {
          pending += 1;
          submitted += 1;
        }
      }
      return {
        subTaskId: sub.id,
        title: sub.title,
        points: sub.points,
        order: sub.order,
        totalStudents: total,
        pendingStudents: pending,
        submittedStudents: submitted,
      };
    });
}

export type ProjectMemberReachRow = {
  memberId: string;
  memberName: string;
  orderIndex: number;
  completionRate: number;
  completedCount: number;
  totalSubTasks: number;
  segmentStates: boolean[];
};

export function buildProjectMemberReachRows(
  item: ProjectAssignmentItem,
  members: Array<{ id: string; name: string; orderIndex?: number; nameChoseongPositions?: number[] }>
): ProjectMemberReachRow[] {
  const subTasks = item.subTasks.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const totalSubTasks = subTasks.length;
  const memberMap = new Map(members.map((m) => [m.id, m]));
  return item.assignedMemberIds.map((memberId) => {
    const member = memberMap.get(memberId);
    const segmentStates = subTasks.map((sub) => {
      const st = item.studentStatusesBySubTask[`${sub.id}:${memberId}`];
      return st ? COMPLETED.includes(st.status) : false;
    });
    const completedCount = segmentStates.filter(Boolean).length;
    const completionRate =
      totalSubTasks > 0 ? Number(((completedCount / totalSubTasks) * 100).toFixed(2)) : 0;
    return {
      memberId,
      memberName: member ? displayClassMemberName(member).trim() || '알 수 없음' : '알 수 없음',
      orderIndex: member?.orderIndex ?? 0,
      completionRate,
      completedCount,
      totalSubTasks,
      segmentStates,
    };
  });
}

export type RoutineDateSummary = {
  date: string;
  dayOfWeek: string;
  isRoutineDay: boolean;
  isCustomDate: boolean;
  pendingCount: number;
  totalStudents: number;
  hasRecords: boolean;
};

const DAY_LABELS_KO = ['일', '월', '화', '수', '목', '금', '토'];

function todayIsoLocal(): string {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export type RoutineMemberReachRow = {
  memberId: string;
  memberName: string;
  orderIndex: number;
  completionRate: number;
  confirmedCount: number;
  totalDates: number;
  todayCount: number;
};

/** 루틴 멤버별 도달률 (에듀코디: confirmed만 카운트, 분모=전체 루틴일) */
export function buildRoutineMemberReachRows(
  item: RoutineAssignmentItem,
  members: Array<{ id: string; name: string; orderIndex?: number; nameChoseongPositions?: number[] }>
): RoutineMemberReachRow[] {
  const scheduleDates = calculateRoutineDates(item.startDate, item.dueDate, item.routineDays);
  const totalDates = scheduleDates.length;
  const today = (() => {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();
  const todayCount = scheduleDates.filter((d) => d <= today).length;
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const ensured = ensureRoutineDateBuckets(item);

  return item.assignedMemberIds.map((memberId) => {
    let confirmedCount = 0;
    for (const bucket of ensured.dateStatuses) {
      // 스케줄 날짜만 도달률에 반영 (커스텀 날짜 제외 — 에듀코디 totalDates와 동일)
      if (!scheduleDates.includes(bucket.date)) continue;
      const st = bucket.studentStatuses.find((s) => s.studentId === memberId);
      if (st?.status === 'confirmed') confirmedCount += 1;
    }
    const member = memberMap.get(memberId);
    const completionRate =
      totalDates > 0 ? Number(((confirmedCount / totalDates) * 100).toFixed(2)) : 0;
    return {
      memberId,
      memberName: member ? displayClassMemberName(member).trim() || '알 수 없음' : '알 수 없음',
      orderIndex: member?.orderIndex ?? 0,
      completionRate,
      confirmedCount,
      totalDates,
      todayCount,
    };
  });
}

/** 체크 패널 네비용: 루틴 스케줄 + 커스텀 날짜 (전체, 정렬) */
export function listAllRoutineDates(item: RoutineAssignmentItem): string[] {
  const ensured = ensureRoutineDateBuckets(item);
  return ensured.dateStatuses.map((b) => b.date).sort((a, b) => a.localeCompare(b));
}

/** 루틴 날짜 + 커스텀 날짜 요약. 미래 날짜는 제외. */
export function listRoutineDateSummaries(
  item: RoutineAssignmentItem,
  options?: { sort?: 'desc' | 'asc'; memberIdSet?: Set<string> | null }
): RoutineDateSummary[] {
  const ensured = ensureRoutineDateBuckets(item);
  const memberIdSet = options?.memberIdSet;
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  const routineSet = new Set(
    calculateRoutineDates(item.startDate, item.dueDate, item.routineDays)
  );
  const today = todayIsoLocal();
  const sort = options?.sort ?? 'desc';
  const rows: RoutineDateSummary[] = ensured.dateStatuses
    .filter((b) => b.date <= today)
    .map((b) => {
      const pending = countRoutinePendingForDate(ensured, b.date, memberIdSet);
      const hasRecords = (b.studentStatuses?.length ?? 0) > 0;
      const d = new Date(`${b.date}T00:00:00`);
      return {
        date: b.date,
        dayOfWeek: DAY_LABELS_KO[d.getDay()] ?? '',
        isRoutineDay: routineSet.has(b.date),
        isCustomDate: Boolean(b.isCustomDate) || !routineSet.has(b.date),
        pendingCount: pending,
        totalStudents: ids.length,
        hasRecords,
      };
    });
  rows.sort((a, b) =>
    sort === 'asc' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)
  );
  return rows;
}

export function countRoutinePendingForDate(
  item: RoutineAssignmentItem,
  date: string,
  memberIdSet?: Set<string> | null
): number {
  const bucket = item.dateStatuses.find((b) => b.date === date);
  const ids = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet);
  let pending = 0;
  for (const mid of ids) {
    const st = bucket?.studentStatuses.find((s) => s.studentId === mid);
    const status = st?.status ?? 'pending';
    if (status === 'pending' || status === 'submitted') pending += 1;
  }
  return pending;
}

export function countRoutinePendingTotal(
  item: RoutineAssignmentItem,
  memberIdSet?: Set<string> | null
): number {
  const ensured = ensureRoutineDateBuckets(item);
  return ensured.dateStatuses.reduce(
    (sum, b) => sum + countRoutinePendingForDate(ensured, b.date, memberIdSet),
    0
  );
}

export function itemPendingCount(
  item: AssignmentPointsItem,
  memberIdSet?: Set<string> | null
): number {
  if (item.kind === 'general') return countGeneralPending(item, memberIdSet);
  if (item.kind === 'project') return countProjectPending(item, memberIdSet);
  return countRoutinePendingTotal(ensureRoutineDateBuckets(item), memberIdSet);
}

export function upsertStatus(
  list: AssignmentStudentStatus[],
  next: AssignmentStudentStatus
): AssignmentStudentStatus[] {
  const idx = list.findIndex((s) => s.studentId === next.studentId);
  if (idx < 0) return [...list, next];
  const copy = [...list];
  copy[idx] = next;
  return copy;
}

/**
 * 체크 패널 등 학급 스코프 저장용.
 * 스코프(현재 학급) 멤버 행만 nextScoped로 교체하고, 다른 학급 멤버 status·메모는 유지한다.
 */
export function mergeScopedStudentStatuses(
  prev: AssignmentStudentStatus[],
  nextScoped: AssignmentStudentStatus[],
  scopeMemberIds: ReadonlySet<string>
): AssignmentStudentStatus[] {
  if (scopeMemberIds.size === 0) return [...nextScoped];
  const kept = prev.filter((s) => !scopeMemberIds.has(s.studentId));
  return [...kept, ...nextScoped];
}

export type AssignmentPointHistoryEntry = {
  id: string;
  memberId: string;
  recordedDate: string;
  /** 테이블 메모열: 과제명(+하위과제) */
  title: string;
  kind: AssignmentPointsItem['kind'];
  kindLabel: string;
  status: AssignmentStatusValue;
  statusLabel: string;
  earnedPoints: number;
  memo?: string;
};

const KIND_LABEL: Record<AssignmentPointsItem['kind'], string> = {
  general: '일반',
  project: '프로젝트',
  routine: '루틴',
};

const STATUS_LABEL: Record<AssignmentStatusValue, string> = {
  pending: '미확인',
  confirmed: '확인',
  delayed: '지연',
  exempted: '예외',
  excluded: '제외',
  submitted: '제출',
};

function pushIfEarned(
  out: AssignmentPointHistoryEntry[],
  entry: Omit<AssignmentPointHistoryEntry, 'kindLabel' | 'statusLabel'> & {
    kind: AssignmentPointsItem['kind'];
    status: AssignmentStatusValue;
  }
) {
  // 에듀코디 StudentPointDetail과 동일: 0점·제외는 이력에서 숨김
  if (!entry.earnedPoints || entry.status === 'excluded') return;
  out.push({
    ...entry,
    kindLabel: KIND_LABEL[entry.kind],
    statusLabel: STATUS_LABEL[entry.status] ?? entry.status,
  });
}

/** 멤버별 과제 포인트 이력 (에듀코디 StudentPointDetail 과제 탭에 대응) */
export function listAssignmentPointHistoryForMember(
  items: AssignmentPointsItem[],
  memberId: string
): AssignmentPointHistoryEntry[] {
  if (!memberId) return [];
  const out: AssignmentPointHistoryEntry[] = [];

  for (const item of items) {
    if (!item.assignedMemberIds.includes(memberId)) continue;

    if (item.kind === 'general') {
      const st = item.studentStatuses.find((s) => s.studentId === memberId);
      if (!st) continue;
      pushIfEarned(out, {
        id: `asgn-${item.id}-${memberId}`,
        memberId,
        recordedDate: item.dueDate,
        title: item.title,
        kind: 'general',
        status: st.status,
        earnedPoints: st.earnedPoints ?? 0,
        memo: st.memo,
      });
      continue;
    }

    if (item.kind === 'project') {
      for (const sub of item.subTasks) {
        const st = item.studentStatusesBySubTask[`${sub.id}:${memberId}`];
        if (!st) continue;
        pushIfEarned(out, {
          id: `asgn-${item.id}-${sub.id}-${memberId}`,
          memberId,
          recordedDate: item.dueDate,
          title: `${item.title} · ${sub.title}`,
          kind: 'project',
          status: st.status,
          earnedPoints: st.earnedPoints ?? 0,
          memo: st.memo,
        });
      }
      continue;
    }

    const ensured = ensureRoutineDateBuckets(item);
    for (const bucket of ensured.dateStatuses) {
      const st = bucket.studentStatuses.find((s) => s.studentId === memberId);
      if (!st) continue;
      pushIfEarned(out, {
        id: `asgn-${item.id}-${bucket.date}-${memberId}`,
        memberId,
        recordedDate: bucket.date,
        title: item.title,
        kind: 'routine',
        status: st.status,
        earnedPoints: st.earnedPoints ?? 0,
        memo: st.memo,
      });
    }
  }

  out.sort((a, b) => {
    const byDate = (b.recordedDate || '').localeCompare(a.recordedDate || '');
    if (byDate !== 0) return byDate;
    return (a.title || '').localeCompare(b.title || '', 'ko');
  });
  return out;
}
